import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
  Filter
} from 'lucide-react';
import { 
  usePatientRiskFactors, 
  usePatientRiskFactorMutations,
  usePatientRiskFactorStatistics 
} from '../../hooks/usePatientRiskFactors';
import './PatientRiskFactorManager.css';

const PatientRiskFactorManager = () => {
  const { t } = useTranslation('patientRisk');
  const [showForm, setShowForm] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  const { factors, loading, refresh } = usePatientRiskFactors({ 
    activeOnly: !showInactive 
  });
  
  const { statistics, loading: statsLoading } = usePatientRiskFactorStatistics();
  
  const { 
    createFactor, 
    updateFactor, 
    deleteFactor,
    toggleActive,
    saving 
  } = usePatientRiskFactorMutations();
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Clinical',
    multiplier: 1.5,
    description: '',
    applicableConditions: [],
    ageRange: null,
    departmentSpecific: [],
    isActive: true
  });
  
  const [errors, setErrors] = useState({});
  const [conditionInput, setConditionInput] = useState('');
  
  const handleAddFactor = () => {
    setEditingFactor(null);
    setFormData({
      name: '',
      category: 'Clinical',
      multiplier: 1.5,
      description: '',
      applicableConditions: [],
      ageRange: null,
      departmentSpecific: [],
      isActive: true
    });
    setShowForm(true);
  };
  
  const handleEditFactor = (factor) => {
    setEditingFactor(factor);
    setFormData({
      name: factor.name,
      category: factor.category,
      multiplier: factor.multiplier,
      description: factor.description || '',
      applicableConditions: factor.applicableConditions || [],
      ageRange: factor.ageRange || null,
      departmentSpecific: factor.departmentSpecific || [],
      isActive: factor.isActive
    });
    setShowForm(true);
  };
  
  const handleDeleteFactor = async (factorId) => {
    if (!window.confirm('Are you sure you want to delete this risk factor? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteFactor(factorId);
      refresh();
    } catch (error) {
      alert('Failed to delete risk factor. It may be in use by patients.');
    }
  };
  
  const handleToggleActive = async (factorId) => {
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      await toggleActive(factorId, userId);
      refresh();
    } catch (error) {
      alert('Failed to toggle risk factor status.');
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };
  
  const handleAddCondition = () => {
    if (conditionInput.trim()) {
      handleInputChange('applicableConditions', [
        ...formData.applicableConditions,
        conditionInput.trim()
      ]);
      setConditionInput('');
    }
  };
  
  const handleRemoveCondition = (index) => {
    handleInputChange('applicableConditions', 
      formData.applicableConditions.filter((_, i) => i !== index)
    );
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    const multiplier = parseFloat(formData.multiplier);
    if (isNaN(multiplier) || multiplier < 1.0 || multiplier > 3.0) {
      newErrors.multiplier = 'Multiplier must be between 1.0 and 3.0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      
      if (editingFactor) {
        await updateFactor(editingFactor.id, formData, userId);
      } else {
        await createFactor(formData, userId);
      }
      
      setShowForm(false);
      setEditingFactor(null);
      refresh();
    } catch (error) {
      alert(`${t('failedToSaveRiskFactor')}: ${error.message}`);
    }
  };
  
  const filteredFactors = factors.filter(factor => {
    if (categoryFilter !== 'all' && factor.category !== categoryFilter) {
      return false;
    }
    return true;
  });
  
  const getMultiplierColor = (multiplier) => {
    if (multiplier >= 2.5) return '#c62828';
    if (multiplier >= 2.0) return '#f44336';
    if (multiplier >= 1.5) return '#ff9800';
    return '#ffc107';
  };
  
  return (
    <div className="patient-risk-factor-manager">
      <div className="manager-header">
        <div className="header-title">
          <h1>{t('patientRiskFactorLibrary')}</h1>
          <p>{t('managePatientRiskFactors')}</p>
        </div>
        <button onClick={handleAddFactor} className="btn btn-primary">
          <Plus size={20} />
          {t('addRiskFactor')}
        </button>
      </div>
      
      {/* Statistics */}
      {!statsLoading && statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{statistics.total}</div>
            <div className="stat-label">{t('totalFactors')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statistics.active}</div>
            <div className="stat-label">{t('active')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statistics.highImpact}</div>
            <div className="stat-label">{t('highImpact20')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">×{statistics.avgMultiplier}</div>
            <div className="stat-label">{t('avgMultiplier')}</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="manager-filters">
        <div className="filter-group">
          <Filter size={20} />
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">{t('allCategories')}</option>
            <option value="Demographic">{t('categoryDemographic')}</option>
            <option value="Clinical">{t('categoryClinical')}</option>
            <option value="Procedural">{t('categoryProcedural')}</option>
            <option value="Comorbidity">{t('categoryComorbidity')}</option>
          </select>
        </div>
        
        <label className="show-inactive-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive Factors
        </label>
      </div>
      
      {/* Factors List/Table */}
      {loading ? (
        <div className="loading-state">{t('loadingRiskFactors')}</div>
      ) : filteredFactors.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={48} color="#ccc" />
          <h3>{t('noRiskFactorsFound')}</h3>
          <p>{t('addFirstPatientRiskFactor')}</p>
        </div>
      ) : (
        <div className="factors-table-container">
          <table className="factors-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Multiplier</th>
                <th>Description</th>
                <th>Conditions</th>
                <th>Age Range</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFactors.map(factor => (
                <tr 
                  key={factor.id} 
                  className={!factor.isActive ? 'inactive-row' : ''}
                >
                  <td>
                    <strong>{factor.name}</strong>
                  </td>
                  <td>
                    <span className={`category-badge ${factor.category.toLowerCase()}`}>
                      {factor.category}
                    </span>
                  </td>
                  <td>
                    <span 
                      className="multiplier-badge"
                      style={{ backgroundColor: getMultiplierColor(factor.multiplier) }}
                    >
                      ×{factor.multiplier.toFixed(1)}
                    </span>
                  </td>
                  <td className="description-cell">
                    {factor.description || '—'}
                  </td>
                  <td className="conditions-cell">
                    {factor.applicableConditions && factor.applicableConditions.length > 0 
                      ? factor.applicableConditions.join(', ')
                      : '—'
                    }
                  </td>
                  <td>
                    {factor.ageRange 
                      ? `${factor.ageRange.min || ''}${factor.ageRange.min && factor.ageRange.max ? ' - ' : ''}${factor.ageRange.max || ''}${!factor.ageRange.min && !factor.ageRange.max ? '—' : ''}`
                      : t('allAges')
                    }
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(factor.id)}
                      className={`btn btn-secondary btn-sm ${factor.isActive ? 'active' : ''}`}
                      title={factor.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {factor.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      {factor.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEditFactor(factor)}
                        className="btn btn-edit btn-icon"
                        title={t('edit')}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteFactor(factor.id)}
                        className="btn btn-delete btn-icon"
                        title={t('delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Form Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-modal">
            <div className="form-header">
              <h2>{editingFactor ? t('editRiskFactor') : t('addRiskFactorTitle')}</h2>
              <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                <X size={20} />
              </button>
            </div>
            
            <div className="form-content">
              <div className="form-group">
                <label>{t('name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder={t('placeholderName')}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('category')} *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={errors.category ? 'error' : ''}
                  >
                    <option value="Demographic">{t('categoryDemographic')}</option>
                    <option value="Clinical">{t('categoryClinical')}</option>
                    <option value="Procedural">{t('categoryProcedural')}</option>
                    <option value="Comorbidity">{t('categoryComorbidity')}</option>
                  </select>
                  {errors.category && <span className="error-text">{errors.category}</span>}
                </div>
                
                <div className="form-group">
                  <label>{t('multiplierRangeLabel')}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1.0"
                    max="3.0"
                    value={formData.multiplier}
                    onChange={(e) => handleInputChange('multiplier', e.target.value)}
                    className={errors.multiplier ? 'error' : ''}
                  />
                  {errors.multiplier && <span className="error-text">{errors.multiplier}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder={t('placeholderDescription')}
                />
              </div>
              
              <div className="form-group">
                <label>{t('applicableMedicalConditions')}</label>
                <div className="conditions-input">
                  <input
                    type="text"
                    value={conditionInput}
                    onChange={(e) => setConditionInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCondition())}
                    placeholder={t('typeConditionAndPressEnter')}
                  />
                  <button onClick={handleAddCondition} className="btn btn-primary">
                    <Plus size={16} />
                  </button>
                </div>
                {formData.applicableConditions.length > 0 && (
                  <div className="conditions-list">
                    {formData.applicableConditions.map((condition, index) => (
                      <span key={index} className="condition-tag">
                        {condition}
                        <button onClick={() => handleRemoveCondition(index)}>
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-footer">
              <button onClick={() => setShowForm(false)} className="btn btn-cancel">
                {t('cancel')}
              </button>
              <button onClick={handleSave} className="btn btn-save" disabled={saving}>
                {saving ? t('savingLabel') : (
                  <>
                    <Save size={16} />
                    {editingFactor ? t('update') : t('createFactor')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientRiskFactorManager;

